import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, ChevronUp, X, Square, Sparkles, Drum, Mic2, Video, Scissors, AudioLines } from "lucide-react";
import { subscribeBadges, cancelBadge, cancelAll, TAB_BADGE_COLORS } from "@/lib/tabBadge";
import { toast } from "sonner";

/**
 * Spotify-style slim status bar that lives at the bottom of the page,
 * fixed-position above the 64-channel mixer. Renders nothing when no
 * background tasks are active, so it doesn't steal any vertical space
 * during normal work.
 *
 * Each row shows: a colored kind-icon, the task label, a live elapsed
 * counter, an indeterminate progress stripe, and (if cancellable) an X.
 * The whole bar can be collapsed to a 28-px-tall strip via the chevron
 * so users can hide it while keeping the indicator that something is
 * still cooking.
 *
 * Visibility is shared across the app via the same `subscribeBadges`
 * store the header pill listens to, so the bar AND the pill always
 * show the same set of tasks in sync.
 */

/** Pick a per-task icon from the badge color. Lucide icons are cheap to
 *  inline so we keep the mapping local to this component. */
function iconForColor(color) {
  switch (color) {
    case TAB_BADGE_COLORS.composing: return AudioLines; // Suno / stems / voice clone
    case TAB_BADGE_COLORS.video: return Video;          // Sora 2
    case TAB_BADGE_COLORS.ready: return Sparkles;       // success flash
    case TAB_BADGE_COLORS.error: return X;
    default: return Drum;
  }
}

/** Refined per-LABEL icon so different "composing"-colored tasks (Suno
 *  vs Stems vs Voice Clone) still get distinct visuals. Pattern-match
 *  on the label first; fall back to the color-based icon. */
function iconForBadge(b) {
  const l = (b.label || "").toLowerCase();
  if (l.includes("stem")) return Scissors;
  if (l.includes("voice")) return Mic2;
  if (l.includes("sora") || l.includes("video")) return Video;
  if (l.includes("suno") || l.includes("compos")) return AudioLines;
  return iconForColor(b.color);
}

export default function CookingStatusBar() {
  const [items, setItems] = useState([]);
  // Persist the collapsed pref so it survives tab refreshes — a habit
  // some users develop after the first session.
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("elpm-cooking-bar-collapsed") === "1"; }
    catch (e) { return false; }
  });

  useEffect(() => subscribeBadges(setItems), []);
  useEffect(() => {
    try { localStorage.setItem("elpm-cooking-bar-collapsed", collapsed ? "1" : "0"); }
    catch (e) { /* noop */ }
  }, [collapsed]);

  if (items.length === 0) return null;

  const cancellableCount = items.filter((b) => b.cancellable).length;
  // Aggregate "longest-elapsed" — feels more accurate than "average"
  // for users who kicked off a long task then a short one.
  const maxElapsed = items.reduce((acc, b) => Math.max(acc, b.elapsed ?? 0), 0);

  return (
    <motion.aside
      data-testid="cooking-status-bar"
      initial={{ y: 80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 80, opacity: 0 }}
      transition={{ duration: 0.25, ease: "easeOut" }}
      className="fixed inset-x-0 bottom-0 z-40 mx-auto w-full max-w-5xl px-3 pb-3"
    >
      <div className="overflow-hidden rounded-2xl border border-white/12 bg-zinc-950/90 shadow-2xl shadow-black/60 backdrop-blur-xl">
        {/* Header strip — always visible. Click to expand/collapse.
            Implemented as a clickable div (not a button) so the inner
            Stop-all button isn't a nested-button hydration violation. */}
        <div
          role="button"
          tabIndex={0}
          data-testid="cooking-status-bar-toggle"
          onClick={() => setCollapsed((v) => !v)}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setCollapsed((v) => !v); } }}
          className="flex w-full cursor-pointer select-none items-center gap-3 px-4 py-2 text-left transition hover:bg-white/[0.04] focus:outline-none focus:ring-1 focus:ring-fuchsia-400/30"
        >
          {/* Stacked pulsing dots — one per active task, capped at 4 */}
          <div className="flex -space-x-1">
            {items.slice(-4).map((b) => (
              <span
                key={b.id}
                className="relative grid h-3 w-3 place-items-center"
                title={b.label}
              >
                <span
                  className="absolute inset-0 animate-ping rounded-full opacity-50"
                  style={{ backgroundColor: b.color }}
                />
                <span
                  className="relative h-2 w-2 rounded-full ring-1 ring-zinc-950"
                  style={{ backgroundColor: b.color }}
                />
              </span>
            ))}
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-mono text-[10px] uppercase tracking-wider text-white/85">
              {items.length === 1 ? "1 task cooking" : `${items.length} tasks cooking`}
              <span className="ml-2 tabular-nums text-white/45">{maxElapsed}s</span>
            </p>
          </div>
          {cancellableCount > 0 && (
            <button
              type="button"
              data-testid="cooking-status-bar-stop-all"
              onClick={(e) => {
                e.stopPropagation();
                const n = cancelAll();
                if (n > 0) toast.message(`Stopped ${n} task${n === 1 ? "" : "s"}`, { duration: 2500 });
              }}
              className="flex items-center gap-1 rounded-md border border-rose-400/30 bg-rose-500/10 px-2 py-1 font-mono text-[9px] uppercase tracking-wider text-rose-100 transition hover:bg-rose-500/25"
              title="Stop every cancellable task"
            >
              <Square className="h-2.5 w-2.5 fill-current" />
              Stop all
            </button>
          )}
          {collapsed ? <ChevronUp className="h-3.5 w-3.5 text-white/55" /> : <ChevronDown className="h-3.5 w-3.5 text-white/55" />}
        </div>

        {/* Task list — collapsible */}
        <AnimatePresence initial={false}>
          {!collapsed && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              className="overflow-hidden border-t border-white/8"
            >
              <ul className="max-h-56 overflow-y-auto">
                {items.slice().reverse().map((b) => {
                  const Icon = iconForBadge(b);
                  return (
                    <li
                      key={b.id}
                      data-testid={`cooking-status-item-${b.id}`}
                      className="flex items-center gap-3 border-b border-white/5 px-4 py-2 last:border-b-0"
                    >
                      <span
                        className="grid h-7 w-7 flex-shrink-0 place-items-center rounded-lg"
                        style={{ backgroundColor: `${b.color}1F`, boxShadow: `inset 0 0 0 1px ${b.color}40` }}
                      >
                        <Icon className="h-3.5 w-3.5" style={{ color: b.color }} />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[12px] font-medium text-white">{b.label}</p>
                        {/* Indeterminate progress stripe — gives the
                            "something IS happening" reassurance even when
                            we don't have a real percentage to show. */}
                        <div className="mt-1 h-[3px] w-full overflow-hidden rounded-full bg-white/8">
                          <div
                            className="h-full w-1/3 animate-[slideRight_1.5s_ease-in-out_infinite] rounded-full"
                            style={{ backgroundColor: b.color }}
                          />
                        </div>
                      </div>
                      <span className="flex-shrink-0 font-mono text-[10px] tabular-nums text-white/55">
                        {b.elapsed != null ? `${b.elapsed}s` : "—"}
                      </span>
                      {b.cancellable ? (
                        <button
                          type="button"
                          data-testid={`cooking-status-cancel-${b.id}`}
                          onClick={() => {
                            cancelBadge(b.id);
                            toast.message(`Stopped · ${b.label}`, { duration: 2200 });
                          }}
                          title="Cancel this task"
                          className="flex-shrink-0 rounded-md bg-white/[0.06] p-1.5 text-white/55 transition hover:bg-rose-500/20 hover:text-rose-200"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      ) : (
                        <span className="flex-shrink-0 rounded-md bg-white/[0.04] px-1.5 py-1 font-mono text-[8px] uppercase tracking-wider text-white/35">
                          server
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.aside>
  );
}
